import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAccessToken, selectPermissions } from '../../../slices/staffSlice';
import { show } from '../../../slices/toastSlice';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { Loader } from '../../../components';
import DownloadTemplateButton from '../Evaluation/DownloadTemplateButton';

const verdictColors = {
    'Approved': 'bg-green-100 text-green-800',
    'Escalate': 'bg-yellow-100 text-yellow-800',
    'Reject': 'bg-red-100 text-red-800',
    'Confirmed': 'bg-green-100 text-green-700',
    'Issue Found': 'bg-red-100 text-red-700',
};

const VerdictBadge = ({ verdict }) => {
    if (!verdict) return null;
    const color = verdictColors[verdict] || 'bg-gray-100 text-gray-700';
    return <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${color}`}>{verdict}</span>;
};

const CheckItem = ({ label, check, onChange, readOnly }) => {
    const c = check || {};
    return (
        <div className="border-b border-gray-100 px-4 py-3">
            <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 w-1/2">{label}</span>
                {readOnly ? (
                    <VerdictBadge verdict={c.status} />
                ) : (
                    <select value={c.status || ''} onChange={e => onChange('status', e.target.value)}
                        className="w-40 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                        <option value="">—</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Issue Found">Issue Found</option>
                        <option value="N/A">N/A</option>
                    </select>
                )}
            </div>
            {!readOnly && (
                <input type="text" value={c.notes || ''} onChange={e => onChange('notes', e.target.value)}
                    placeholder="Notes (optional)"
                    className="mt-2 w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
            )}
            {readOnly && c.notes && (
                <p className="mt-1 text-xs text-gray-500 italic">{c.notes}</p>
            )}
        </div>
    );
};

const SectionHeader = ({ label }) => (
    <div className="px-4 py-3 bg-gray-50 border-y border-gray-200">
        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{label}</h3>
    </div>
);

const allProductChecks = [
    { key: 'kycDocumentsReVerified', label: 'KYC documents re-verified against source' },
    { key: 'incomeVerifiedAgainstSource', label: 'Income verified against payslip/bank statement' },
    { key: 'bankStatementReconciled', label: 'Bank statement reconciled independently' },
    { key: 'creditBureauReRun', label: 'Credit bureau report re-run / cross-checked' },
    { key: 'dtiRecalculated', label: 'DTI recalculated from source documents' },
    { key: 'riskScoreReComputed', label: 'Risk score re-computed and validated' },
    { key: 'preDisbursementControlsChecked', label: 'Pre-disbursement controls independently checked' },
    { key: 'fraudScreeningRePerformed', label: 'Fraud screening re-performed' },
];

const pensionSemiChecks = [
    { key: 'pensionStatementVerifiedWithTrustee', label: 'Pension statement verified with trustee' },
    { key: 'ltpOrScrRecalculated', label: 'LTP / SCR recalculated from source' },
    { key: 'pledgeFormAuthenticated', label: 'Pledge form authenticated' },
    { key: 'contributionHistoryConfirmed', label: 'Contribution history confirmed with trustee' },
];

const ezRentChecks = [
    { key: 'tenancyAgreementReVerified', label: 'Tenancy agreement re-verified' },
    { key: 'landlordBankReVerified', label: 'Landlord bank account re-verified' },
    { key: 'rentToIncomeRecalculated', label: 'Rent-to-income ratio recalculated' },
];

const unsecuredChecks = [
    { key: 'guarantorReVerified', label: 'Guarantor independently re-verified' },
    { key: 'standingOrderConfirmed', label: 'Standing order confirmed with bank' },
    { key: 'employerIndependentlyContacted', label: 'Employer independently contacted' },
    { key: 'momoAnalysisCrossChecked', label: 'MoMo analysis cross-checked' },
];

function hasPermission(permissions, slug) {
    if (!permissions) return false;
    return permissions.includes(slug) || permissions.includes('*:*');
}

function Index({ loanId, loanType, pensionState }) {
    const accessToken = useSelector(selectAccessToken);
    const permissions = useSelector(selectPermissions);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [review, setReview] = useState({
        allProducts: {}, pensionSemi: {}, ezRent: {}, unsecured: {},
        overallVerdict: '', reviewNotes: ''
    });

    const canView = hasPermission(permissions, 'risk_review:view');
    const canEdit = hasPermission(permissions, 'risk_review:edit');
    const readOnly = !canEdit;

    const isPension = pensionState === 'secured' || pensionState === 'semi-secured';
    const isEzRent = (loanType || '').toLowerCase().includes('ezrent');

    const fetchReview = useCallback(async () => {
        try {
            const res = await fetchRequests(`/loans/staff/risk-review?loanId=${loanId}`, "GET", authStaff(accessToken), {}, {});
            if (res?.review) {
                setReview(res.review);
            }
        } catch (err) { /* no existing review */ }
        finally { setLoading(false); }
    }, [loanId, accessToken]);

    useEffect(() => { fetchReview(); }, [fetchReview]);

    const updateCheck = (section, key) => (field, value) => {
        setReview(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [key]: {
                    ...(prev[section]?.[key] || {}),
                    [field]: value
                }
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetchRequests('/loans/staff/risk-review', 'POST', authStaff(accessToken), {}, {
                loanId, loanType, pensionState: pensionState || '',
                allProducts: review.allProducts,
                pensionSemi: review.pensionSemi,
                ezRent: review.ezRent,
                unsecured: review.unsecured,
                reviewNotes: review.reviewNotes
            });
            if (res?.err) {
                dispatch(show({ type: 'error', state: true, message: res.err }));
            } else if (res?.review) {
                setReview(res.review);
                dispatch(show({ type: 'success', state: true, message: 'Risk review saved' }));
            }
        } catch (err) {
            dispatch(show({ type: 'error', state: true, message: 'Failed to save risk review' }));
        } finally { setSaving(false); }
    };

    if (!canView) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v.01M12 12a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm0 0v3m9-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <p className="text-sm text-gray-500">You do not have permission to view the Risk Manager Review.</p>
            </div>
        );
    }

    if (loading) {
        return <div className="flex justify-center py-10"><Loader color="text-gray-500" size="w-5 h-5" /></div>;
    }

    return (
        <div className="space-y-5 py-4">
            <div className="flex items-center justify-between gap-2 px-4 py-2 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                        Risk Manager Review — Second-Line Control
                    </span>
                    {readOnly && (
                        <span className="text-xs text-gray-400">View only</span>
                    )}
                </div>
                <DownloadTemplateButton loanId={loanId} />
            </div>

            {review.overallVerdict && (
                <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                    <span className="text-sm font-medium text-gray-700">Overall Verdict:</span>
                    <VerdictBadge verdict={review.overallVerdict} />
                </div>
            )}

            {/* Section A: All Products */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="A. All Products — General Re-Performance" />
                {allProductChecks.map(c => (
                    <CheckItem key={c.key} label={c.label}
                        check={review.allProducts?.[c.key]}
                        onChange={updateCheck('allProducts', c.key)}
                        readOnly={readOnly} />
                ))}
            </div>

            {/* Section B: Pension/Semi-Secured */}
            {isPension && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <SectionHeader label="B. Pension / Semi-Secured — Collateral Re-Performance" />
                    {pensionSemiChecks.map(c => (
                        <CheckItem key={c.key} label={c.label}
                            check={review.pensionSemi?.[c.key]}
                            onChange={updateCheck('pensionSemi', c.key)}
                            readOnly={readOnly} />
                    ))}
                </div>
            )}

            {/* Section C: ezRent */}
            {isPension && isEzRent && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <SectionHeader label="C. ezRent — Tenancy Re-Performance" />
                    {ezRentChecks.map(c => (
                        <CheckItem key={c.key} label={c.label}
                            check={review.ezRent?.[c.key]}
                            onChange={updateCheck('ezRent', c.key)}
                            readOnly={readOnly} />
                    ))}
                </div>
            )}

            {/* Section D: Unsecured */}
            {!isPension && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <SectionHeader label="D. Unsecured — Guarantor & Income Re-Performance" />
                    {unsecuredChecks.map(c => (
                        <CheckItem key={c.key} label={c.label}
                            check={review.unsecured?.[c.key]}
                            onChange={updateCheck('unsecured', c.key)}
                            readOnly={readOnly} />
                    ))}
                </div>
            )}

            {/* Review Notes */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Review Notes & Verdict" />
                <div className="px-4 py-3">
                    <textarea
                        value={review.reviewNotes || ''}
                        onChange={e => setReview(prev => ({ ...prev, reviewNotes: e.target.value }))}
                        disabled={readOnly}
                        rows={3}
                        className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-gray-50"
                        placeholder="Overall review notes, escalation reasons, or conditions..."
                    />
                </div>
                {!readOnly && (
                    <div className="px-4 pb-3">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Override Overall Verdict</label>
                        <select
                            value={review.overallVerdict || ''}
                            onChange={e => setReview(prev => ({ ...prev, overallVerdict: e.target.value }))}
                            className="w-48 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                            <option value="">Auto-derive</option>
                            <option value="Approved">Approved</option>
                            <option value="Escalate">Escalate</option>
                            <option value="Reject">Reject</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Save */}
            {canEdit && (
                <div className="flex items-center gap-3">
                    <button type="button" onClick={handleSave} disabled={saving}
                        className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#162d56] transition-colors disabled:opacity-50">
                        {saving && <Loader color="text-white" size="w-4 h-4" />}
                        {saving ? 'Saving...' : 'Save Risk Review'}
                    </button>
                    {review.overallVerdict && (
                        <span className="text-sm text-gray-500">Verdict: <VerdictBadge verdict={review.overallVerdict} /></span>
                    )}
                </div>
            )}

            {review.reviewedBy && (
                <p className="text-xs text-gray-400 px-1">
                    Last reviewed by {review.reviewedBy} on {new Date(review.reviewedAt).toLocaleDateString('en-GH')}
                </p>
            )}
        </div>
    );
}

export default Index;
